// DEPENDENCIES
import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";

// CSS
import "./RFADraftsDrawer.css";

// API IMPORTS
import {
	getAPICall,
	postAPICall,
	deleteAPICall,
} from "../../../config/apiCalls";

// MUI COMPONENTS
import {
	Drawer,
	Box,
	Typography,
	IconButton,
	Divider,
	List,
	ListItem,
	ListItemText,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	Checkbox,
	FormControlLabel,
	Alert,
	Tabs,
	Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";

const RFADraftsDrawer = ({
	open,
	onClose,
	instance,
	account,
	employeeID,
	onOpenDraft,
	logAutomation,
}) => {
	const [drafts, setDrafts] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [activeTab, setActiveTab] = useState("mine");

	const [deleteTarget, setDeleteTarget] = useState(null);

	const [shareTarget, setShareTarget] = useState(null);
	const [allEmployees, setAllEmployees] = useState([]);
	const [sharedEmployeeIDs, setSharedEmployeeIDs] = useState([]);
	const [isShareLoading, setIsShareLoading] = useState(false);

	const fetchDrafts = useCallback(async () => {
		setIsLoading(true);
		setLoadError("");
		try {
			const res = await getAPICall(instance, account, "/api/rfa-drafts", {
				employeeID,
			});
			setDrafts(
				(res.data || []).map((draft) => ({
					...draft,
					is_owner: Boolean(draft.is_owner),
				})),
			);
		} catch (error) {
			setLoadError("Couldn't load drafts. Please try again.");
		} finally {
			setIsLoading(false);
		}
	}, [instance, account, employeeID]);

	useEffect(() => {
		if (open) {
			fetchDrafts();
		}
	}, [open, fetchDrafts]);

	const draftSummary = (draft) => {
		const recipientName = [
			draft.recipient_first_name,
			draft.recipient_last_name,
		]
			.map((s) => (s || "").trim())
			.filter(Boolean)
			.join(" ");
		const dealLabel = (draft.deal_name || "").trim();
		const parts = [dealLabel, recipientName].filter(Boolean);
		return parts.length ? parts.join(" · ") : "No deal/recipient yet";
	};

	const openDraftClicked = async (draft) => {
		try {
			const res = await getAPICall(
				instance,
				account,
				`/api/rfa-drafts/${draft.id}`,
				{ employeeID },
			);
			const fullDraft = res.data && res.data[0];
			if (!fullDraft) {
				setLoadError("Couldn't open that draft. Please try again.");
				return;
			}
			onOpenDraft(fullDraft);
			onClose();
		} catch (error) {
			setLoadError("Couldn't open that draft. Please try again.");
		}
	};

	const deleteConfirmed = async () => {
		if (!deleteTarget) return;
		try {
			await deleteAPICall(
				instance,
				account,
				`/api/rfa-drafts/${deleteTarget.id}`,
				{ employeeID },
			);
			logAutomation?.(
				true,
				`Draft "${deleteTarget.draft_name}" deleted (id ${deleteTarget.id})`,
			);
			setDrafts((prev) => prev.filter((d) => d.id !== deleteTarget.id));
		} catch (error) {
			logAutomation?.(
				false,
				`Error deleting draft "${deleteTarget.draft_name}" (id ${deleteTarget.id}): ${error}`,
			);
			setLoadError("Couldn't delete that draft. Please try again.");
		} finally {
			setDeleteTarget(null);
		}
	};

	const shareClicked = async (draft) => {
		setShareTarget(draft);
		setIsShareLoading(true);
		try {
			const [employeesRes, sharesRes] = await Promise.all([
				getAPICall(instance, account, "/api/employees", { active: true }),
				getAPICall(
					instance,
					account,
					`/api/rfa-drafts/${draft.id}/shares`,
					{ employeeID },
				),
			]);
			setAllEmployees(
				(employeesRes.data || []).filter((e) => e.id !== employeeID),
			);
			setSharedEmployeeIDs(
				(sharesRes.data || []).map((s) => s.employee_id),
			);
		} catch (error) {
			setLoadError("Couldn't load the share list. Please try again.");
			setShareTarget(null);
		} finally {
			setIsShareLoading(false);
		}
	};

	const shareToggled = async (targetEmployee, isCurrentlyShared) => {
		try {
			if (isCurrentlyShared) {
				await deleteAPICall(
					instance,
					account,
					`/api/rfa-drafts/${shareTarget.id}/share/${targetEmployee.id}`,
					{ employeeID },
				);
				setSharedEmployeeIDs((prev) =>
					prev.filter((id) => id !== targetEmployee.id),
				);
			} else {
				await postAPICall(
					instance,
					account,
					`/api/rfa-drafts/${shareTarget.id}/share`,
					{ employeeID, shareWithEmployeeID: targetEmployee.id },
				);
				setSharedEmployeeIDs((prev) => [...prev, targetEmployee.id]);
			}
			setDrafts((prev) =>
				prev.map((d) =>
					d.id === shareTarget.id
						? {
								...d,
								shared_with_count:
									d.shared_with_count +
									(isCurrentlyShared ? -1 : 1),
						  }
						: d,
				),
			);
			logAutomation?.(
				true,
				`Draft "${shareTarget.draft_name}" ${
					isCurrentlyShared ? "unshared with" : "shared with"
				} ${targetEmployee.first_name} ${targetEmployee.last_name} (id ${shareTarget.id})`,
			);
		} catch (error) {
			logAutomation?.(
				false,
				`Error ${
					isCurrentlyShared ? "unsharing" : "sharing"
				} draft "${shareTarget.draft_name}" (id ${shareTarget.id}) with employee ${targetEmployee.id}: ${error}`,
			);
			setLoadError("Couldn't update sharing. Please try again.");
		}
	};

	const myDrafts = drafts.filter((d) => d.is_owner);
	const sharedDrafts = drafts.filter((d) => !d.is_owner);
	const visibleDrafts = activeTab === "mine" ? myDrafts : sharedDrafts;

	return (
		<>
			<Drawer anchor="right" open={open} onClose={onClose}>
				<Box sx={{ width: 380, padding: "1.5em" }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: "1em",
						}}
					>
						<Typography variant="h6">Drafts</Typography>
						<IconButton onClick={onClose} size="small">
							<CloseIcon />
						</IconButton>
					</Box>
					<Tabs
						value={activeTab}
						onChange={(event, newValue) => setActiveTab(newValue)}
						variant="fullWidth"
						sx={{ minHeight: "36px" }}
					>
						<Tab
							value="mine"
							label={`My Drafts (${myDrafts.length})`}
							sx={{ minHeight: "36px" }}
						/>
						<Tab
							value="shared"
							label={`Shared with Me (${sharedDrafts.length})`}
							sx={{ minHeight: "36px" }}
						/>
					</Tabs>
					<Divider sx={{ marginBottom: "1em" }} />
					{loadError && (
						<Alert
							severity="error"
							onClose={() => setLoadError("")}
							sx={{ marginBottom: "1em" }}
						>
							{loadError}
						</Alert>
					)}
					{isLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center", padding: "2em" }}>
							<CircularProgress size={28} />
						</Box>
					) : visibleDrafts.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							{activeTab === "mine"
								? 'No saved drafts yet. Use "Save as Draft" on the form to create one.'
								: "No drafts have been shared with you."}
						</Typography>
					) : (
						<List disablePadding>
							{visibleDrafts.map((draft) => (
								<ListItem
									key={draft.id}
									disablePadding
									sx={{
										flexDirection: "column",
										alignItems: "stretch",
										border: "1px solid #e0e0e0",
										borderRadius: "6px",
										marginBottom: "0.75em",
										padding: "0.75em",
									}}
								>
									<ListItemText
										primary={draft.draft_name}
										secondary={draftSummary(draft)}
									/>
									{!draft.is_owner && (
										<Chip
											icon={<PersonAddIcon fontSize="small" />}
											label={`Shared by ${draft.owner_name}`}
											size="small"
											title={`Shared by ${draft.owner_name}`}
											sx={{
												maxWidth: "100%",
												alignSelf: "flex-start",
												marginTop: "0.25em",
												marginBottom: "0.25em",
												justifyContent: "flex-start",
												backgroundColor: "rgba(24, 76, 147, 0.12)",
												color: "#184C93",
												"& .MuiChip-icon": {
													color: "#184C93",
												},
												"& .MuiChip-label": {
													overflow: "hidden",
													textOverflow: "ellipsis",
												},
											}}
										/>
									)}
									{draft.is_owner &&
										draft.shared_with_count > 0 && (
											<Chip
												icon={<PersonAddIcon fontSize="small" />}
												label={`Shared x${draft.shared_with_count}`}
												size="small"
												sx={{
													marginTop: "0.25em",
													marginBottom: "0.25em",
													justifyContent: "flex-start",
													alignSelf: "flex-start",
												}}
											/>
										)}
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											gap: "0.5em",
										}}
									>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Last saved{" "}
											{dayjs(draft.modified).format(
												"MMM D, YYYY h:mm A",
											)}
										</Typography>
										{draft.is_owner && (
											<>
												<IconButton
													size="small"
													onClick={() => shareClicked(draft)}
													title="Share"
												>
													<PersonAddIcon fontSize="small" />
												</IconButton>
												<IconButton
													size="small"
													onClick={() => setDeleteTarget(draft)}
													title="Delete"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</>
										)}
										<Button
											size="small"
											variant="contained"
											onClick={() => openDraftClicked(draft)}
										>
											Open
										</Button>
									</Box>
								</ListItem>
							))}
						</List>
					)}
				</Box>
			</Drawer>

			{/* DELETE CONFIRMATION */}
			<Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
				<DialogTitle id="rfaDraftDeleteDialogTitle">
					Delete draft?
				</DialogTitle>
				<DialogContent id="rfaDraftDeleteDialogContent">
					<Typography variant="body2">
						Are you sure you want to delete "{deleteTarget?.draft_name}"?
						This can't be undone.
					</Typography>
					<div className="rfaDialogButtons">
						<Button
							variant="outlined"
							color="error"
							onClick={() => setDeleteTarget(null)}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={deleteConfirmed}
						>
							Delete
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* SHARE PICKER */}
			<Dialog
				open={Boolean(shareTarget)}
				onClose={() => setShareTarget(null)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle id="rfaDraftShareDialogTitle">
					Share "{shareTarget?.draft_name}"
				</DialogTitle>
				<DialogContent id="rfaDraftShareDialogContent">
					{isShareLoading ? (
						<Box sx={{ display: "flex", justifyContent: "center", padding: "1em" }}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<List dense>
							{allEmployees.map((employeeOption) => {
								const isShared = sharedEmployeeIDs.includes(
									employeeOption.id,
								);
								return (
									<FormControlLabel
										key={employeeOption.id}
										control={
											<Checkbox
												checked={isShared}
												onChange={() =>
													shareToggled(
														employeeOption,
														isShared,
													)
												}
											/>
										}
										label={`${employeeOption.first_name} ${employeeOption.last_name}`}
										sx={{ display: "flex", width: "100%" }}
									/>
								);
							})}
						</List>
					)}
					<div className="rfaDialogButtons">
						<Button
							variant="contained"
							onClick={() => setShareTarget(null)}
						>
							Done
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default RFADraftsDrawer;
